<template>
	<div class="actionButtonsContainer">
		<LoadingSpinner v-if="showLoadingSpinner" />
		<!-- All the staff buttons -->
		<div
			v-if="isStaff"
			class="contractSection fontSmall actionButtons"
		>
			<div>
				<fragment v-if="fullySignedByCustomer">
					<input
						type="submit"
						class="submitButton backgroundGrey colorWhite hideOnPrint"
						value="Email Signed Contract"
						@click="resendSignedContractEmail"
					/>
				</fragment>
				<div v-else-if="fullySignedByStaff">
					<input
						id="consignor_submit"
						type="submit"
						class="submitButton backgroundGrey colorWhite hideOnPrint"
						value="Email Signature Request to Consignor"
						name="email_submit"
						@click="sendSignatureRequest"
					/>
				</div>
			</div>
			<div>
				<input
					type="submit"
					class="submitButton backgroundGreen colorWhite hideOnPrint"
					value="Save"
					@click="saveContract"
				/>
			</div>
		</div>
		<span
			v-if="sentToCustomer"
			class="contentBlock fontSmall marginTopSmall"
		>
			Email Signature Request last sent
			<br />
			{{ formatDate(contract.sent_customer_email_unixtime) }} at
			{{ formatTime(contract.sent_customer_email_unixtime) }}
		</span>
	</div>
</template>

<script>
	import { Fragment } from 'vue-frag'
	import contractState from '@/mixins/contractState'
	import { mapActions } from 'vuex'
	import LoadingSpinner from '../components/LoadingSpinner.vue'

	export default {
		name: 'ContractActionButtons',
		components: {
			Fragment,
			LoadingSpinner
		},
		mixins: [contractState],
		props: {
			contract: {
				type: Object,
				required: true
			},
			saveContract: {
				type: Function,
				required: true
			}
		},
		data() {
			return {
				consigneeSignature: '',
				isSubmittingSignature: false,
				isSendingEmail: false
			}
		},
		computed: {
			/**
			 * Whether all inventory lists and contract are signed by staff
			 * @returns {boolean} True if everything is signed by staff
			 */
			fullySignedByStaff() {
				if (!this.signedByStaff) return false
				if (!this.contract.inventory_lists?.length) return this.signedByStaff
				return this.contract.inventory_lists.every((list) => {
					// Skip lists with null id and 0 items
					if (
						!list.id &&
						(!list.items?.length ||
							list.items.length === 0 ||
							list.items.every((item) => !item.description))
					) {
						return true
					}
					return list.id && list.signed_by_name && list.signed_date
				})
			},

			/**
			 * Whether all inventory lists and contract are signed by customer
			 * @returns {boolean} True if everything is signed by customer
			 */
			fullySignedByCustomer() {
				if (!this.signedByCustomer) return false
				if (!this.contract.inventory_lists?.length) return this.signedByCustomer
				return this.contract.inventory_lists.every((list) => {
					// Skip lists with null id and 0 items
					if (
						!list.id &&
						(!list.items?.length ||
							list.items.length === 0 ||
							list.items.every((item) => !item.description))
					) {
						return true
					}
					return list.customer_signature && list.customer_signature_date
				})
			},
			/**
			 * Whether to show the loading spinner
			 * @returns {boolean} True if the loading spinner should be shown
			 */
			showLoadingSpinner() {
				return this.isSubmittingSignature || this.isSendingEmail
			}
		},
		watch: {},
		methods: {
			...mapActions(['updateContract', 'sendEmail', 'signContract', 'getContract']),
			/**
			 * Format the date
			 * @param {number} date - The date to format
			 * @returns {string} The formatted date
			 */
			formatDate(date) {
				let parsedDate = date
				if (typeof date === 'number') {
					// If timestamp is in milliseconds (13 digits), convert to seconds
					const timestamp = String(date).length === 13 ? Math.floor(date / 1000) : date
					parsedDate = new Date(timestamp * 1000)
				}
				return new Date(parsedDate).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric'
				})
			},
			/**
			 * Format the time
			 * @param {number} date - The date to format
			 * @returns {string} The formatted time
			 */
			formatTime(date) {
				let parsedDate = date
				if (typeof date === 'number') {
					// If timestamp is in milliseconds (13 digits), convert to seconds
					const timestamp = String(date).length === 13 ? Math.floor(date / 1000) : date
					parsedDate = new Date(timestamp * 1000)
				}
				return new Date(parsedDate).toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: 'numeric',
					second: 'numeric',
					hour12: true
				})
			},
			/**
			 * Resend the signed contract email to the consignor
			 */
			async resendSignedContractEmail() {
				try {
					this.emitStatusBanner('loading', 'Resending signed contract email...')
					this.isSendingEmail = true
					await this.sendEmail({
						token: this.contract.token,
						type: 'customer_signed'
					})
					this.isSendingEmail = false
					this.emitStatusBanner(
						'success',
						"Successfully sent email containing copy of customer's contract."
					)
				} catch (error) {
					console.error('Error resending signed contract email:', error)
					this.emitStatusBanner('error', 'Failed to resend signed contract email')
				}
			},
			/**
			 * Send email signature request to consignor
			 */
			async sendSignatureRequest() {
				try {
					this.emitStatusBanner('loading', 'Sending signature request email...')
					this.isSendingEmail = true
					await this.sendEmail({
						token: this.contract.token,
						type: 'signature_request'
					})
					// Reload the contract to get latest state
					await this.getContract({ token: this.contract.token })
					this.isSendingEmail = false
					this.emitStatusBanner('success', 'Signature request email sent successfully')
				} catch (error) {
					console.error('Error sending signature request:', error)
					this.emitStatusBanner('error', 'Failed to send signature request email')
				}
			}
		}
	}
</script>

<style scoped>
	.actionButtonsContainer {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding-bottom: 5rem;
	}
	.actionButtons {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin: 2.25rem 0 0;
	}

	/* On mobile devices, actionButtons children should wrap */
	@media (max-width: 768px) {
		.actionButtons > div {
			width: 100%;
		}
	}
</style>
